import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ISettingsType } from 'common/src/types';
import { Lock, Payments, AccessTime, CurrencyExchange, WhatsApp, ContentCopy, Check } from '@mui/icons-material';
import { Box, Typography, alpha, useTheme, Button, Stack, Divider, IconButton, Tooltip, Snackbar, Alert } from '@mui/material';

interface AppBlockedScreenProps {
    settings: ISettingsType;
}

const WHATSAPP_NUMBER = '+55 (31) 99785-8404';

const getDaysUntilDue = (date: Date | number | null | undefined) => {
    if (!date) return null;
    const dueDate = typeof date === 'number' ? new Date(date) : new Date(date);
    dueDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = dueDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const formatCurrency = (value: string | number | null | undefined) => {
    if (!value && value !== 0) return 'R$ 0,00';
    let num: number;
    if (typeof value === 'string') {
        const cleaned = value.replace(/[^0-9,.-]/g, '').replace(',', '.');
        num = parseFloat(cleaned);
    } else {
        num = value;
    }
    if (isNaN(num)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
};

export function AppBlockedScreen({ settings }: AppBlockedScreenProps) {
    const { t } = useTranslation();
    const theme = useTheme();
    const payment = settings?.payment || {};
    const appName = settings?.appName || 'App';
    const daysUntilBlock = getDaysUntilDue(payment.dueDateClosingEnd);
    const isBlocked = daysUntilBlock !== null && daysUntilBlock <= 0;

    const [copiedLink, setCopiedLink] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [copiedNumber, setCopiedNumber] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}`;
    const paymentLink = payment.link || '';

    const showSnackbar = (message: string) => {
        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    const handleCopyNumber = () => {
        navigator.clipboard.writeText(WHATSAPP_NUMBER).then(() => {
            setCopiedNumber(true);
            setTimeout(() => setCopiedNumber(false), 3000);
        }).catch(() => {
            showSnackbar(t('APP_BLOCKED_SCREEN_ERROR_COPY_NUMBER'));
        });
    };

    const handleCopyPaymentLink = () => {
        if (paymentLink) {
            navigator.clipboard.writeText(paymentLink).then(() => {
                setCopiedLink(true);
                setTimeout(() => setCopiedLink(false), 3000);
            }).catch(() => {
                showSnackbar(t('APP_BLOCKED_SCREEN_ERROR_COPY_LINK'));
            });
        }
    };

    const getStatusText = () => {
        if (isBlocked) {
            const days = Math.abs(daysUntilBlock);
            return t('APP_BLOCKED_SCREEN_BLOCKED_DAYS', {
                days,
                plural: days > 1 ? 's' : ''
            });
        } else {
            const days = daysUntilBlock || 0;
            return t('APP_BLOCKED_SCREEN_BLOCK_WILL', {
                days,
                plural: days > 1 ? 's' : ''
            });
        }
    };

    return (
        <Box
            sx={{
                p: 3,
                display: 'flex',
                minHeight: '100dvh',
                overflow: 'hidden',
                alignItems: 'center',
                position: 'relative',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)} 0%, ${alpha(theme.palette.grey[900], 0.95)} 100%)`,
            }}
        >
            <Box
                sx={{
                    top: -200,
                    width: 500,
                    height: 500,
                    right: -200,
                    borderRadius: '50%',
                    position: 'absolute',
                    background: `radial-gradient(circle, ${alpha(theme.palette.error.main, 0.05)} 0%, transparent 70%)`,
                }}
            />
            <Box
                sx={{
                    left: -150,
                    width: 400,
                    height: 400,
                    bottom: -150,
                    borderRadius: '50%',
                    position: 'absolute',
                    background: `radial-gradient(circle, ${alpha(theme.palette.warning.main, 0.04)} 0%, transparent 70%)`,
                }}
            />
            <Box
                sx={{
                    top: '50%',
                    left: '50%',
                    width: 800,
                    height: 800,
                    borderRadius: '50%',
                    position: 'absolute',
                    transform: 'translate(-50%, -50%)',
                    background: `radial-gradient(circle, ${alpha(theme.palette.error.main, 0.02)} 0%, transparent 70%)`,
                }}
            />

            <Box
                sx={{
                    width: '100%',
                    borderRadius: 6,
                    overflow: 'hidden',
                    p: { xs: 3, sm: 5 },
                    position: 'relative',
                    maxWidth: { sm: "95%", md: 800 },
                    boxShadow: `0 20px 60px ${alpha(theme.palette.error.main, 0.15)}`,
                    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.grey[900], 0.95)} 100%)`,
                }}
            >
                <Box
                    sx={{
                        px: 1.5,
                        py: 0.5,
                        top: 20,
                        gap: 0.5,
                        right: 20,
                        display: 'flex',
                        borderRadius: 20,
                        alignItems: 'center',
                        position: 'absolute',
                        bgcolor: alpha(theme.palette.error.main, 0.15),
                    }}
                >
                    <Lock sx={{ fontSize: 14, color: theme.palette.error.main }} />
                    <Typography variant="caption" fontWeight={600} color="error.main">
                        {t('APP_BLOCKED_SCREEN_BLOCKED_STATUS')}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        mb: 2,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        sx={{
                            p: 3,
                            borderRadius: '50%',
                            border: `2px solid ${alpha(theme.palette.error.main, 0.2)}`,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.15)}, ${alpha(theme.palette.warning.main, 0.1)})`,
                            animation: 'pulse 2s ease-in-out infinite',
                            '@keyframes pulse': {
                                '0%': { transform: 'scale(1)', opacity: 1 },
                                '50%': { transform: 'scale(1.05)', opacity: 0.8 },
                                '100%': { transform: 'scale(1)', opacity: 1 },
                            }
                        }}
                    >
                        <Lock sx={{ fontSize: 64, color: theme.palette.error.main }} />
                    </Box>
                </Box>

                <Typography
                    variant="h4"
                    align="center"
                    fontWeight={800}
                    sx={{
                        mb: 1,
                        color: theme.palette.error.main,
                    }}
                >
                    {t('APP_BLOCKED_SCREEN_BLOCKED_TITLE')}
                </Typography>

                <Typography
                    sx={{ mb: 3 }}
                    align="center"
                    variant="subtitle1"
                    color="text.secondary"
                    dangerouslySetInnerHTML={{
                        __html: t('APP_BLOCKED_SCREEN_BLOCKED_DESCRIPTION', { appName })
                    }}
                />

                <Divider sx={{ mb: 3 }} />

                <Stack spacing={2} sx={{ mb: 3 }}>
                    {payment.price && (
                        <Box
                            sx={{
                                p: 2,
                                gap: 2,
                                display: 'flex',
                                borderRadius: 3,
                                alignItems: 'center',
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                            }}
                        >
                            <CurrencyExchange sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    {t('APP_BLOCKED_SCREEN_BLOCKED_VALUE')}
                                </Typography>
                                <Typography variant="h6" fontWeight={700}>
                                    {formatCurrency(payment.price)}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {daysUntilBlock !== null && (
                        <Box
                            sx={{
                                p: 2,
                                gap: 2,
                                borderRadius: 3,
                                display: 'flex',
                                alignItems: 'center',
                                bgcolor: alpha(theme.palette.error.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
                            }}
                        >
                            <AccessTime sx={{ color: theme.palette.error.main, fontSize: 28 }} />
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    {t('APP_BLOCKED_SCREEN_BLOCKED_STATUS_LABEL')}
                                </Typography>
                                <Typography variant="body2" fontWeight={600} color="error.main">
                                    {getStatusText()}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Stack>

                <Stack spacing={3}>
                    {paymentLink && (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Tooltip title={copiedLink ? t('APP_BLOCKED_SCREEN_COPIED') : t('APP_BLOCKED_SCREEN_COPY_LINK')}>
                                <IconButton
                                    onClick={handleCopyPaymentLink}
                                    sx={{
                                        height: 56,
                                        minWidth: 56,
                                        color: '#fff',
                                        borderRadius: 3,
                                        transition: 'all 0.3s ease',
                                        boxShadow: `0 4px 16px ${alpha(theme.palette.success.main, 0.3)}`,
                                        background: copiedLink
                                            ? `linear-gradient(135deg, #4CAF50, #2E7D32)`
                                            : `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.info.main})`,
                                        '&:hover': {
                                            transform: 'scale(1.05)',
                                        },
                                    }}
                                >
                                    {copiedLink ? <Check /> : <ContentCopy />}
                                </IconButton>
                            </Tooltip>
                            <Button
                                fullWidth
                                size="large"
                                variant="contained"
                                startIcon={<Payments />}
                                onClick={() => window.open(paymentLink, '_blank')}
                                sx={{
                                    py: 2,
                                    borderRadius: 3,
                                    fontWeight: 700,
                                    boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.3)}`,
                                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.info.main})`,
                                    '&:hover': {
                                        boxShadow: `0 12px 40px ${alpha(theme.palette.success.main, 0.4)}`,
                                    }
                                }}
                            >
                                {t('APP_BLOCKED_SCREEN_MAKE_PAYMENT')}
                            </Button>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            fullWidth
                            size="large"
                            variant="contained"
                            startIcon={<WhatsApp sx={{ fontSize: 32 }} />}
                            onClick={() => window.open(whatsappLink, '_blank')}
                            sx={{
                                py: 1,
                                color: '#fff',
                                borderRadius: 3,
                                fontWeight: 700,
                                fontSize: '1.1rem',
                                transition: 'transform 0.2s ease',
                                boxShadow: `0 8px 32px ${alpha('#25D366', 0.4)}`,
                                background: `linear-gradient(135deg, #25D366, #128C7E)`,
                                '&:hover': {
                                    transform: 'scale(1.02)',
                                    boxShadow: `0 12px 48px ${alpha('#25D366', 0.5)}`,
                                },
                            }}
                        >
                            {t('APP_BLOCKED_SCREEN_CONTACT_SUPPORT')}
                        </Button>

                        <Tooltip title={copiedNumber ? t('APP_BLOCKED_SCREEN_COPIED') : t('APP_BLOCKED_SCREEN_COPY_NUMBER')}>
                            <IconButton
                                onClick={handleCopyNumber}
                                sx={{
                                    height: 56,
                                    minWidth: 56,
                                    borderRadius: 3,
                                    color: '#fff',
                                    transition: 'all 0.3s ease',
                                    boxShadow: `0 4px 16px ${alpha('#25D366', 0.3)}`,
                                    background: copiedNumber
                                        ? `linear-gradient(135deg, #4CAF50, #2E7D32)`
                                        : `linear-gradient(135deg, #25D366, #128C7E)`,
                                    '&:hover': {
                                        background: copiedNumber
                                            ? `linear-gradient(135deg, #4CAF50, #2E7D32)`
                                            : `linear-gradient(135deg, #1da851, #0e6b5f)`,
                                        transform: 'scale(1.05)',
                                    },
                                }}
                            >
                                {copiedNumber ? <Check /> : <ContentCopy />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Stack>

                <Box
                    sx={{
                        mt: 3,
                        pt: 2,
                        textAlign: 'center',
                        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    }}
                >
                    <Typography variant="caption" color="text.secondary">
                        {t('APP_BLOCKED_SCREEN_PAYMENT_INSTRUCTION')}
                    </Typography>
                </Box>
            </Box>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{
                    '& .MuiSnackbar-root': {
                        mt: 2,
                    }
                }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity="success"
                    variant="filled"
                    sx={{
                        width: '100%',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}